import * as React from "react";

import { MantineColor, TextInput, TextInputProps } from "@mantine/core";
import { useMergedRef } from "@mantine/hooks";

import { useAppState } from "../../../hooks/useAppState";
import { EditableString } from "../../../lib/model/checklistAppState";

export const VanillaEditableText = React.forwardRef(
  (
    {
      text,
      color,
      placeholder,
      extraPadding: _extraPadding,
      justify,
      onSave,
      onEnter,
      onEmptyEnter,
      onEmptyTab,
      onEmptyBlur,
      styles,
      ..._props
    }: {
      text: EditableString;
      color?: MantineColor;
      placeholder: string;
      extraPadding?: number;
      justify?: "left" | "right" | "center";
      onSave: (newValue: string) => void;
      onEnter?: () => void;
      onEmptyEnter?: () => void;
      onEmptyTab?: () => void;
      onEmptyBlur?: () => void;
      styles?: Partial<TextInputProps["styles"]>;
    },
    incomingRef
  ): JSX.Element => {
    const [newValue, setNewValue] = React.useState<string>("");
    const internalInputRef = React.useRef<HTMLInputElement>(null);
    const inputRef = useMergedRef(internalInputRef, incomingRef);

    const autofocusField = useAppState((state) => state.autofocusField);

    const saveAndFinishEditing = React.useCallback(() => {
      if (text.content === newValue) {
        return;
      }
      onSave(newValue);
    }, [onSave, internalInputRef.current, newValue, text.content]);

    const cancelEditing = React.useCallback(() => {
      setNewValue(text.content);
      internalInputRef.current?.blur();
    }, [setNewValue, internalInputRef.current]);

    // TODO: deduplicate events sent by "enter" and onBlur
    const qualifyEmptyContinue = text.content === "" && newValue !== text.content;
    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = React.useCallback(
      (event) => {
        if (event.key === "Enter") {
          if (onEnter) {
            onEnter();
          } else {
            internalInputRef.current?.blur();
          }
          if (qualifyEmptyContinue && onEmptyEnter) {
            onEmptyEnter();
          }
        } else if (event.key === "Escape") {
          cancelEditing();
        } else if (event.key === "Tab" && qualifyEmptyContinue && onEmptyTab) {
          internalInputRef.current?.blur();
          onEmptyTab();
          event.preventDefault();
        }
      },
      [cancelEditing, onEmptyTab, onEmptyEnter, internalInputRef.current, qualifyEmptyContinue]
    );

    React.useEffect(() => {
      setNewValue(text.content);
    }, [text.content]);

    const onChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value),
      [setNewValue]
    );

    const onBlur = React.useCallback(() => {
      saveAndFinishEditing();
      if (newValue === "" && onEmptyBlur) {
        onEmptyBlur();
      }
    }, [saveAndFinishEditing, onEmptyBlur, newValue]);

    const textInputStyle = React.useMemo(
      () => ({
        root: { flex: "1 1 50%", flexGrow: 100, flexShrink: 100 },
        wrapper: { flex: "1 1 50%", flexGrow: 100, flexShrink: 100 },
        input: {
          textAlign: justify,
          height: "1.5em",
          padding: 0,
          minWidth: "10px",
          color: color,
          "&:focus": {
            borderBottom: `1px dashed #000`,
            cursor: "text",
          },
          "&:focus:hover": {
            cursor: "text",
          },
          "&:hover": {
            cursor: "pointer",
          },
          ...styles,
        },
      }),
      [justify, color, styles]
    );

    return (
      <TextInput
        autoFocus={autofocusField === text.id}
        ref={inputRef}
        variant="unstyled"
        styles={textInputStyle}
        onChange={onChange}
        value={newValue}
        placeholder={placeholder}
        spellCheck={false}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }
);

export const VanillaEditableTitle = ({ text: _text }: { text: EditableString }): JSX.Element => {
  return <TextInput variant="unstyled" styles={{}} />;
};
